import { Button, Input, MediaCard, Modal } from '@sendible/design-system/src';
import { Option } from '@sendible/design-system/src/components/molecules/AdvancedSelect';
import { useTranslation } from 'react-i18next';
import { useCallback, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { MediaPreview } from '@sendible/frontendv2/src/components';
import { useGetFlickrSearch } from '@sendible/frontendv2/src/models/flickrSearch';
import { useComposeBoxContext } from '../../../composeBoxContext';
import { Container, Footer, FooterContent, GridContent, Header, SelectionCount } from '../styles/index.styles';
import { LoadingState } from '../LoadingState';
import { BlankState } from '../BlankState';

export const FlickrModal = () => {
  const { setIsFlickrModalOpen, uploadUrlsToCompose, getRemainingMediaLimit } = useComposeBoxContext();
  const { t } = useTranslation(['compose_box', 'common_terms']);
  const [selectedItems, setSelectedItems] = useState<UrlUploadFileType[]>([]);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const [query, setQuery] = useState('');
  const [querySubmitted, setQuerySubmitted] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([{ action: 'keyword', label: t('keyword') }]);
  const searchProperty = selectedOptions[0].action === 'keyword' ? 'text' : 'tags';
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetFlickrSearch(querySubmitted, searchProperty);
  const [options] = useState([
    { action: 'keyword', label: t('keyword') }, // text
    { action: 'tag', label: t('tag') }, // tags
  ]);
  const currentMaxLimit = getRemainingMediaLimit();

  const { ref, inView } = useInView();

  const mediaItems: MediaSearchItem[] = (data?.pages as MediaSearchItem[][] | undefined)?.flat() || [];

  const mediaList: Media[] | undefined = mediaItems?.map((media) => ({
    url: media.downloadUrl,
    type: media.type,
    id: media.id,
    isGif: media.isGif,
    name: media.title,
    downloadUrl: media.downloadUrl,
  }));

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleMediaSelection = (file: UrlUploadFileType) => {
    if (!selectedItems.find((item) => item.url === file.url)) {
      setSelectedItems((prev) => [...prev, file]);
    } else {
      const newSelection = selectedItems.filter((i) => i.url !== file.url);

      setSelectedItems(newSelection);
    }
  };

  const handleSubmit = useCallback((value: string) => {
    setQuerySubmitted(value);
    setSelectedItems([]);
  }, []);

  const handleImportClick = useCallback(() => {
    if (selectedItems.length > 0) {
      uploadUrlsToCompose(selectedItems);
      setIsFlickrModalOpen(false);
    }
  }, [selectedItems, uploadUrlsToCompose, setIsFlickrModalOpen]);

  const handleMediaPreview = (url: string) => {
    const mediaIndex = mediaList?.findIndex((media) => media.url === url);

    if (mediaIndex !== undefined && mediaIndex !== -1) {
      setPreviewInitialItem(mediaIndex);
      setShowPreview(true);
    }
  };

  const handleKeyPress = useCallback(
    (key: string, text: string) => {
      setQuery(text);
      if (key === 'Enter') {
        handleSubmit(text);
      }
    },
    [handleSubmit]
  );

  const getButtonLabel = (count: number) => (count === 1 ? (t('import_image') as string) : (t('import_images', { count }) as string));

  const subheaderContent = (
    <Header>
      <Input
        size="sm"
        autoFocus
        label={t('search_images', { ns: 'common_terms' }) as string}
        keyPress={handleKeyPress}
        value={query}
        button={{ label: t('search') as string, click: () => handleSubmit(query), disabled: query.trim() === '' }}
        change={(value) => setQuery(value)}
        select={{
          id: 'select-id',
          label: '',
          onSelect: (option) => setSelectedOptions([option]),
          options,
          selectedOptions,
        }}
      />
    </Header>
  );

  const buttonLabel = getButtonLabel(selectedItems.length);

  return (
    <>
      <Modal
        portalDomId="layer-root"
        close={() => setIsFlickrModalOpen(false)}
        headingIcon="flickr"
        headingTitle={t('flickr', { ns: 'common_terms' }) as string}
        preventEscapeClose={showPreview}
        subHeaderContent={subheaderContent}
        variation="large"
        footerContent={
          querySubmitted ? (
            <Footer>
              <FooterContent>
                <SelectionCount>{t('selection_count', { selected: selectedItems.length, limit: currentMaxLimit })}</SelectionCount>
                <Button
                  appearance="primary"
                  disabled={selectedItems.length === 0}
                  label={buttonLabel}
                  onClick={handleImportClick}
                  size={14}
                  variation="fill"
                />
              </FooterContent>
            </Footer>
          ) : undefined
        }
      >
        {querySubmitted && (
          <Container>
            {(!isLoading && mediaItems?.length === 0) || isError ? (
              <BlankState />
            ) : (
              <GridContent>
                {isLoading ? (
                  <LoadingState />
                ) : (
                  <>
                    {mediaItems?.map(({ id, title, thumbUrl, downloadUrl }, index) => {
                      const isSelected = !!selectedItems.find((selectedItem) => selectedItem.url === downloadUrl);
                      const isDisabled = !isSelected && selectedItems.length >= currentMaxLimit;

                      return (
                        <MediaCard
                          id={id}
                          key={`${id}-${index}`}
                          name={title}
                          isSelected={isSelected}
                          open={() => handleMediaPreview(downloadUrl)}
                          select={isDisabled ? undefined : () => handleMediaSelection({ name: title, url: downloadUrl })}
                          showCheckboxWithoutHover={isSelected || selectedItems.length > 0}
                          thumbUrl={thumbUrl}
                          type="image"
                          updateDate=""
                          uploadingText=""
                          customClass={isDisabled ? 'disabled-mediacard' : ''}
                        />
                      );
                    })}
                    <div
                      ref={ref}
                      style={{ height: '1px' }}
                    />
                  </>
                )}
              </GridContent>
            )}
          </Container>
        )}
      </Modal>
      {showPreview && mediaList?.length && (
        <MediaPreview
          initialMediaIndex={previewInitialItem}
          mediaList={mediaList}
          onClose={() => setShowPreview(false)}
          shouldShowActions={false}
        />
      )}
    </>
  );
};
